import convert from "convert-units";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import lround from "lodash/round";
import submit from "submit-form";

const {
  PARAMETER_OPTIONS: { FREEFORM, LOAD, RPEAMRAP, REPS },
  RPE_RANGE,
} = require("../../../../constants");

import { useTranslation } from "react-i18next";
import { CopyComponent } from "../../../../parts/CopyComponent";
import ParameterQuickEditInput from "../../../../parts/inputs/parameter-quick-edit-input";
import { SearchDropdown } from "../../../../parts/inputs/search-dropdown";
import MetconDisplay from "../../../../parts/metcon/MetconDisplay";
import MetconExercisesList from "../../../../parts/metcon/MetconExercisesList";
import { getExerciseName } from "../../../../parts/modals/learn-more/blockHelpers";
import { mapParameter } from "../../../../parts/other-exercise/CustomParametersSets";
import SvgIcon from "../../../../parts/SvgIcon";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import convertToRiR from "../../../../utils/converters/convert-intensity";
import { getMovementsData } from "../../../../utils/exercise-library";
import { getLoadDrop } from "../../../../utils/load-drop";
import { repsValue } from "../../../../utils/reps-range";
import NewExerciseLineParameters from "./NewExerciseLineParametersPe";

const EditorExerciseLineItem = ({
  handleQuickEditChange,
  _handleOpenAddSet,
  _handleCloseAddSet,
  _handleNewSetChange,
  _handleSaveNewSet,
  _handleNewMovementChange,
  newMovement,
  newSet,
  setIndex,
  activeExercise,
  fromMetric,
  activeUser,
  withWeight,
  loadInputs,
  intensityInputs,
  localLoadInputs,
  localIntensityInputs,
  isQuickEditOpen,
  isAddingSet,
  currentRow,
  units,
  setLength,
  activeUserToRequest,
  set,
  deleteExercise,
  updateExercise,
  isProgramEditor,
  handleDeleteSetInState,
  _handleSaveNewSetForOther,
  OrderArrowsComponent,
  prefix,
  OptionsComponent = null,
  isCoach,
  activeUserToHandleCopyAction,
  readOnly = false,
  quickEditSessionId = 0,
}) => {
  const { locals } = useRillContext();
  const { t } = useTranslation("athlete-portal");
  const isAssistant = activeExercise.type === "assistant";
  const isPrimary = activeExercise.type === "primary";
  const isComplex = !!activeExercise.complex;
  // gets primary repmax of the exercise if exercise type is primary else
  // if secondary, use secondary rm, else if assistant just use active user's squat (RM doesn't really matter for assistant)
  // if all else fails, default to 0
  let defaultRepMax = get(
    activeExercise,
    `${
      isPrimary && !isComplex
        ? `repMaximums.${activeExercise.primary}`
        : "secondaryRepMaximum"
    }`,
    get(activeUser, "repMaximums.squat", 0)
  );

  if (activeExercise.type === "secondary") {
    const movement =
      activeUserToRequest.exerciseLibrary[activeExercise.primary]?.find(
        (m) => m.movement === activeExercise.secondary
      ) || {};
    defaultRepMax = movement.repMaximum || defaultRepMax;
  }

  defaultRepMax = Math.round(fromMetric(defaultRepMax, units), 0);
  const precision = units === "metric" ? 2 : 1;
  const load = set.load ? lround(fromMetric(set.load, units), precision) : 0;

  const exerciseName = getExerciseName(activeExercise);
  const isMetcon = activeExercise.primary === "metcon";

  let tabIndex = 100;
  const hasLoad = Boolean(load);
  const exId = activeExercise._id;
  const handleOpenAddSet = (e) => _handleOpenAddSet(e, setIndex);
  const handleDeleteSet = (e) => {
    e.preventDefault();
    const copyActiveExercise = cloneDeep(activeExercise);

    if (isProgramEditor) {
      if (activeExercise.sets.length === 1) {
        if (confirm(t("programs.confirmDeleteExercise")))
          deleteExercise(activeExercise._id);
      } else {
        // Update for optimistic optimizations
        copyActiveExercise.sets.splice(setIndex, 1);
        updateExercise(copyActiveExercise);
        // update exercises in the complex
        if (copyActiveExercise.exercises?.length > 1) {
          const secondExercise = cloneDeep(copyActiveExercise.exercises[1]);
          secondExercise.sets.splice(setIndex, 1);
          updateExercise(secondExercise);
        }
        if (copyActiveExercise.exercises?.length > 2) {
          const thirdExercise = cloneDeep(copyActiveExercise.exercises[2]);
          thirdExercise.sets.splice(setIndex, 1);
          updateExercise(thirdExercise);
        }

        // update exercise in state (the one send to the api if clicking on save)
        handleDeleteSetInState(setIndex);
      }
    }

    let organizationCoach;
    if (activeUser.organizationId || activeUser?.coach?.organizationId) {
      organizationCoach = activeUserToRequest._id;
    }

    const actionToRequest = activeUser?.permissions?.includes("coach")
      ? `/actions/exercise/quick-edit/sets/remove/${setIndex}/${exId}/${organizationCoach}/${undefined}`
      : `/actions/exercise/quick-edit/sets/remove/${setIndex}/${exId}/${organizationCoach}/${activeUser._id}`;

    submit(actionToRequest, {
      method: isProgramEditor ? "POST" : "GET",
      body: {
        omitModified: true,
        omitActiveUser: true,
        editExercise: activeExercise,
      },
    });
  };
  // used to select the first field when adding set
  const handleFocus = (e) => {
    e.target.select();
  };

  const movements = getMovementsData(
    activeExercise,
    activeUserToRequest,
    locals.athlete || activeUserToRequest
  );

  const intensityMeasure =
    get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";
  const isRiR = intensityMeasure === "RiR";
  const amrapLabel = isRiR ? "RiR" : "RPE";
  const inputClassName = isQuickEditOpen ? "" : "hidden";
  const valueClassName = isQuickEditOpen ? "hidden" : "";
  const isFreeForm = activeExercise.parameters?.[0] === FREEFORM;
  const complexTwo = activeExercise.exercises?.[1];
  const complexThree = activeExercise.exercises?.[2];

  const serverExercise = locals.editExercises?.find((ex) => ex._id === exId);
  const serverSet = serverExercise?.sets?.[setIndex] ?? set;
  const serverLoad = serverSet.load
    ? lround(fromMetric(serverSet.load, units), precision)
    : 0;
  const serverComplexTwoSet = complexTwo
    ? (locals.editExercises?.find((ex) => ex._id === complexTwo._id)?.sets?.[
        setIndex
      ] ?? complexTwo.sets?.[setIndex])
    : null;
  const serverComplexThreeSet = complexThree
    ? (locals.editExercises?.find((ex) => ex._id === complexThree._id)?.sets?.[
        setIndex
      ] ?? complexThree.sets?.[setIndex])
    : null;

  return (
    <>
      {setIndex === 0 && (
        <>
          <div className="name-wrapper">
            <span className="calendar-prefix">{prefix}</span>
            {isQuickEditOpen ? (
              <SearchDropdown
                valueKey="movementLabel"
                options={movements}
                handleChange={(val) =>
                  _handleNewMovementChange(val, exId, setIndex)
                }
                placeholder={exerciseName}
                // Picking a movement here only buffers the change locally
                // (see EditorSessionTable.handleQuickEditChange's
                // "exerciseType" branch) - it no longer auto-saves. Show the
                // picked label immediately so the row doesn't look like the
                // pick didn't register while it waits for Save.
                value={newMovement}
                disabled={isComplex || isMetcon}
              />
            ) : (
              <p className="font-semibold">{exerciseName}</p>
            )}

            <div
              className={`exercise-actions flex items-center ml-auto gap-1 shrink-0 ${
                readOnly ? "min-h-[26.38px]" : ""
              }`}
            >
              {!readOnly && <OrderArrowsComponent />}
              {isCoach && (
                <CopyComponent
                  exercises={activeExercise.exercises || activeExercise}
                  athlete={activeUserToHandleCopyAction}
                />
              )}
              {!readOnly && <OptionsComponent />}
            </div>
          </div>

          {!isFreeForm && !isMetcon && (
            <div className="parameters-pb titles">
              {Array.isArray(activeExercise.parameters) ? (
                activeExercise.parameters.map((elem) => (
                  <span
                    className={elem === FREEFORM ? "invisible" : ""}
                    key={`${elem}title`}
                  >
                    {["RPE", "RPE/AMRAP"].includes(elem)
                      ? intensityMeasure
                      : elem}
                  </span>
                ))
              ) : (
                <>
                  <span>{t("programs.sets")}</span>
                  <span className={complexTwo ? "min-w-24" : ""}>
                    {t("programs.reps")}
                  </span>
                  <span>{amrapLabel}</span>
                  <span>{t("programs.load")}</span>
                  {activeExercise.type === "assistant" ? null : (
                    <span>{t("programs.percent1rm")}</span>
                  )}
                </>
              )}
              {isQuickEditOpen && <span className="add-delete-set" />}
            </div>
          )}

          {isMetcon && <MetconDisplay exercise={activeExercise} />}
        </>
      )}

      {isMetcon ? (
        <MetconExercisesList exercise={activeExercise} setIndex={setIndex} />
      ) : (
        <div className="parameters-pb">
          {Array.isArray(activeExercise.parameters) ? (
            activeExercise.parameters.map((elem, index) => (
              <span
                className={`${
                  elem === FREEFORM
                    ? "text-left! basis-3/5! whitespace-pre-wrap"
                    : ""
                }`}
                key={`${index}_${elem}_parameter_${setLength}_${
                  serverSet[mapParameter(elem)]
                }_${quickEditSessionId}`}
              >
                <span className={valueClassName}>
                  {elem === LOAD
                    ? set.loadDrop
                      ? getLoadDrop(set.loadDrop)
                      : set[mapParameter(elem)]
                        ? withWeight(
                            units === "metric"
                              ? set[mapParameter(elem)]
                              : convert(set[mapParameter(elem)])
                                  .from("kg")
                                  .to("lb")
                          )
                        : null
                    : ["RPE", RPEAMRAP].includes(elem)
                      ? convertToRiR(set[mapParameter(elem)], !isRiR)
                      : elem === REPS
                        ? repsValue(set.reps, set.repsLower)
                        : set[mapParameter(elem)]}
                </span>

                <ParameterQuickEditInput
                  id={`${exId}_${setIndex}_${elem.toLowerCase()}`}
                  name={mapParameter(elem)}
                  defaultValue={
                    elem === REPS
                      ? repsValue(serverSet.reps, serverSet.repsLower)
                      : serverSet[mapParameter(elem)]
                  }
                  onInput={(e) => handleQuickEditChange(e, exId, setIndex)}
                  parameter={elem}
                  onChange={(e) =>
                    e && handleQuickEditChange(e, exId, setIndex)
                  }
                  key={`${exId}_${setIndex}_rpe_${serverSet.rpe}_${quickEditSessionId}`}
                  units={units}
                  intensityMeasure={intensityMeasure}
                  isRiR={isRiR}
                  className={inputClassName}
                  loadDrop={set.loadDrop}
                />
              </span>
            ))
          ) : (
            <>
              <span>
                <span className={valueClassName}>{set.sets}</span>
                <input
                  id={`${exId}_${setIndex}_sets`}
                  key={`${exId}_${setIndex}_${serverSet.sets}_sets_${quickEditSessionId}`}
                  name="sets"
                  defaultValue={serverSet.sets}
                  onInput={(e) =>
                    e?.target.value && handleQuickEditChange(e, exId, setIndex)
                  }
                  className={inputClassName}
                  type="number"
                  step="1"
                />
              </span>

              <span>
                <span className={valueClassName}>
                  {repsValue(set.reps, set.repsLower)}
                  {complexTwo?.sets?.[setIndex]?.reps &&
                    `+${complexTwo.sets[setIndex].reps}`}
                  {complexThree?.sets?.[setIndex]?.reps &&
                    `+${complexThree.sets[setIndex].reps}`}
                </span>
                <div
                  className={`flex gap-1 items-center${
                    complexTwo ? " min-w-24" : ""
                  }`}
                >
                  <input
                    id={`${exId}_${setIndex}_reps`}
                    key={`${exId}_${setIndex}_${serverSet.reps}_reps_${quickEditSessionId}`}
                    name="reps"
                    defaultValue={repsValue(
                      serverSet.reps,
                      serverSet.repsLower
                    )}
                    onInput={(e) =>
                      e?.target.value &&
                      handleQuickEditChange(e, exId, setIndex)
                    }
                    className={inputClassName}
                    type="text"
                  />
                  {complexTwo?.sets?.[setIndex] && (
                    <input
                      id={`${complexTwo._id}_${setIndex}reps`}
                      key={`${complexTwo._id}_${setIndex}_${serverComplexTwoSet?.reps}_reps_${quickEditSessionId}`}
                      name="reps"
                      defaultValue={
                        serverComplexTwoSet?.reps ??
                        complexTwo.sets[setIndex].reps
                      }
                      onInput={(e) =>
                        e?.target.value &&
                        handleQuickEditChange(e, complexTwo._id, setIndex)
                      }
                      className={inputClassName}
                      type="text"
                    />
                  )}
                  {complexThree?.sets?.[setIndex] && (
                    <input
                      id={`${complexThree._id}_${setIndex}reps`}
                      key={`${complexThree._id}_${setIndex}_${serverComplexThreeSet?.reps}_reps_${quickEditSessionId}`}
                      name="reps"
                      defaultValue={
                        serverComplexThreeSet?.reps ??
                        complexThree.sets[setIndex].reps
                      }
                      onInput={(e) =>
                        e?.target.value &&
                        handleQuickEditChange(e, complexThree._id, setIndex)
                      }
                      className={inputClassName}
                      type="text"
                    />
                  )}
                </div>
              </span>

              <span>
                <span className={valueClassName}>
                  {convertToRiR(set.rpe, !isRiR)}
                </span>
                <select
                  id={`${exId}_${setIndex}_rpe`}
                  key={`${exId}_${setIndex}_${serverSet.rpe}_rpe_${quickEditSessionId}`}
                  className={inputClassName}
                  name="rpe"
                  defaultValue={serverSet.rpe}
                  onChange={(e) =>
                    e && handleQuickEditChange(e, exId, setIndex)
                  }
                >
                  <option value="">{intensityMeasure}</option>
                  {serverSet.rpe &&
                    serverSet.rpe !== "AMRAP" &&
                    !RPE_RANGE.map((i) => i + 1).includes(
                      Number(serverSet.rpe)
                    ) && (
                      <option value={serverSet.rpe}>
                        {convertToRiR(serverSet.rpe, !isRiR)}
                      </option>
                    )}
                  {RPE_RANGE.map((i) => (
                    <option key={i} value={i + 1}>
                      {convertToRiR(i + 1, !isRiR)}
                    </option>
                  ))}
                  <option value="AMRAP">AMRAP</option>
                </select>
              </span>

              <span
                className={
                  set.loadDrop && isQuickEditOpen && !isAssistant
                    ? "grow-2!"
                    : ""
                }
              >
                <span className={valueClassName}>
                  {set.loadDrop
                    ? getLoadDrop(set.loadDrop)
                    : hasLoad && withWeight(load, units)}
                </span>
                {set.loadDrop ? (
                  <input
                    key={`${exId}_${setIndex}_${serverLoad}_loadDrop_${quickEditSessionId}`}
                    name="loadDrop"
                    defaultValue={serverSet.loadDrop}
                    onInput={(e) =>
                      e &&
                      (e.target.value || e.target.value === "") &&
                      handleQuickEditChange(e, exId, setIndex)
                    }
                    className={`${inputClassName} text-left!`}
                    type="text"
                  />
                ) : (
                  <input
                    id={`${exId}_${setIndex}_load`}
                    key={`${exId}_${setIndex}_${serverLoad}_load_${quickEditSessionId}`}
                    name="load"
                    defaultValue={serverLoad}
                    onInput={(e) =>
                      e &&
                      (e.target.value || e.target.value === "") &&
                      handleQuickEditChange(e, exId, setIndex)
                    }
                    className={inputClassName}
                    type="text"
                    ref={(input) =>
                      loadInputs
                        ? (loadInputs[`${exId}_${setIndex}`] = input)
                        : null
                    }
                  />
                )}
                <span className={`quick-edit-load-unit ${inputClassName}`}>
                  {set.loadDrop ? "% mod." : units === "metric" ? "kg" : "lb"}
                </span>
              </span>

              {!isAssistant && (
                <span
                  className={set.loadDrop && isQuickEditOpen ? "hidden" : ""}
                >
                  <span className={valueClassName}>
                    {!set.loadDrop && set.load
                      ? `${lround(set.percentRepMax, 1)}%`
                      : ""}
                  </span>

                  <input
                    id={`${exId}_${setIndex}_percentRepMax`}
                    key={`${exId}_${setIndex}__${
                      serverSet.percentRepMax
                        ? lround(serverSet.percentRepMax, 1)
                        : 0
                    }_percentRepMax_${quickEditSessionId}`}
                    name="percentRepMax"
                    defaultValue={
                      serverSet.percentRepMax
                        ? lround(serverSet.percentRepMax, 1)
                        : 0
                    }
                    onChange={(e) =>
                      e?.target.value &&
                      handleQuickEditChange(e, exId, setIndex)
                    }
                    className={`${inputClassName}${
                      set.loadDrop ? " hidden" : ""
                    }`}
                    type="text"
                    ref={(input) =>
                      intensityInputs
                        ? (intensityInputs[`${exId}_${setIndex}`] = input)
                        : null
                    }
                  />
                  <span
                    className={`quick-edit-load-unit ${inputClassName}${
                      set.loadDrop ? " hidden" : ""
                    }`}
                  >
                    %
                  </span>
                </span>
              )}
            </>
          )}

          {isQuickEditOpen && (
            <span
              className={`add-delete-set${isAddingSet ? " invisible" : ""}`}
            >
              <button
                data-button-type="secondary"
                onClick={handleOpenAddSet}
                type="button"
              >
                <img
                  src="/img/new-icons/add-gradient-icon.svg"
                  height={24}
                  width={24}
                  alt="Add set"
                />
              </button>
              <button
                data-button-type="secondary"
                onClick={handleDeleteSet}
                type="button"
              >
                <img
                  src="/img/new-icons/red-trash.svg"
                  height={18}
                  width={18}
                  alt="Delete set"
                />
              </button>
            </span>
          )}
        </div>
      )}

      {/* This is the line of a set to be added */}
      {isAddingSet &&
        isQuickEditOpen &&
        currentRow === setIndex &&
        (Array.isArray(activeExercise.parameters) ? (
          <NewExerciseLineParameters
            newSet={newSet}
            setIndex={setIndex}
            _handleNewSetChange={_handleNewSetChange}
            exId={exId}
            activeExercise={activeExercise}
            activeUser={activeUserToRequest}
            _handleCloseAddSet={_handleCloseAddSet}
            _handleSaveNewSetForOther={_handleSaveNewSetForOther}
            isProgramEditor={isProgramEditor}
          />
        ) : (
          <div className="parameters-pb">
            <span>
              <input
                autoFocus
                id={`new_${exId}_${setIndex}_sets`}
                name="sets"
                type="number"
                tabIndex={tabIndex++}
                defaultValue={newSet.sets}
                onFocus={handleFocus}
                onInput={(e) =>
                  e?.target.value && _handleNewSetChange(e, defaultRepMax)
                }
                step="1"
              />
            </span>
            <span
              className={`flex gap-1 items-center${
                complexTwo ? " min-w-24" : ""
              }`}
            >
              <input
                id={`new_${exId}_${setIndex}_reps`}
                name="reps"
                type="text"
                tabIndex={tabIndex++}
                defaultValue={newSet.reps}
                onInput={(e) =>
                  e?.target.value && _handleNewSetChange(e, defaultRepMax)
                }
              />
              {complexTwo && (
                <input
                  id={`new_${exId}_${setIndex}_repsTwo`}
                  name="repsTwo"
                  type="text"
                  tabIndex={tabIndex++}
                  defaultValue={newSet.repsTwo || 0}
                  onInput={(e) =>
                    e?.target.value && _handleNewSetChange(e, defaultRepMax)
                  }
                />
              )}
              {complexThree && (
                <input
                  id={`new_${exId}_${setIndex}_repsThree`}
                  name="repsThree"
                  type="text"
                  tabIndex={tabIndex++}
                  defaultValue={newSet.repsThree || 0}
                  onInput={(e) =>
                    e?.target.value && _handleNewSetChange(e, defaultRepMax)
                  }
                />
              )}
            </span>
            <span>
              <select
                id={`new_${exId}_${setIndex}_rpe`}
                name="rpe"
                tabIndex={tabIndex++}
                defaultValue={newSet.rpe}
                onChange={(e) =>
                  e?.target.value && _handleNewSetChange(e, defaultRepMax)
                }
              >
                <option value="">{intensityMeasure}</option>
                {RPE_RANGE.map((i) => (
                  <option key={i} value={i + 1}>
                    {convertToRiR(i + 1, !isRiR)}
                  </option>
                ))}
                <option value="AMRAP">AMRAP</option>
              </select>
            </span>
            <span>
              <input
                id={`new_${exId}_${setIndex}_load`}
                key={`new_${exId}_${setIndex}_load`}
                name="load"
                type="number"
                tabIndex={tabIndex++}
                defaultValue={newSet.load}
                onInput={(e) =>
                  e?.target.value && _handleNewSetChange(e, defaultRepMax)
                }
                step=".1"
                ref={(input) =>
                  localLoadInputs ? (localLoadInputs["new"] = input) : null
                }
              />
              <span className={`quick-edit-load-unit ${inputClassName}`}>
                {units === "metric" ? "kg" : "lb"}
              </span>
            </span>
            {!isAssistant && (
              <span>
                <input
                  id={`new_${exId}_${setIndex}_percentRepMax`}
                  key={`new_${exId}_${setIndex}_percentRepMax`}
                  name="percentRepMax"
                  type="number"
                  tabIndex={tabIndex++}
                  defaultValue={newSet.percentRepMax}
                  onChange={(e) =>
                    e?.target.value && _handleNewSetChange(e, defaultRepMax)
                  }
                  step=".1"
                  ref={(input) =>
                    localIntensityInputs
                      ? (localIntensityInputs["new"] = input)
                      : null
                  }
                />
                <span className={`quick-edit-load-unit ${inputClassName}`}>
                  %
                </span>
              </span>
            )}

            <span className="add-delete-set">
              <button
                data-button-type="primary"
                tabIndex={tabIndex++}
                onClick={(e) =>
                  _handleSaveNewSet(
                    e,
                    set.equipped,
                    defaultRepMax,
                    activeUser,
                    exId,
                    setIndex
                  )
                }
                type="button"
              >
                <SvgIcon src="/img/new-icons/check.svg" noFilterOnLight />
              </button>
              <button
                data-button-type="secondary"
                onClick={(e) => _handleCloseAddSet(e)}
                type="button"
              >
                <SvgIcon
                  src="/img/new-icons/red-cross-20px.svg"
                  noFilterOnLight
                />
              </button>
            </span>
          </div>
        ))}
    </>
  );
};

export default EditorExerciseLineItem;
